import { getGPUTier } from 'detect-gpu';

import { DeviceInfo } from '@ezugi/utils';

import {
  gpuLevels, gpuMemoryMap, scores, thresholds, weights,
} from './constants';

declare global {
  interface Navigator {
    deviceMemory?: number;
  }
}

interface DeviceDetails {
  gpuModel: string | undefined;
  fps?: number;
  userAgent: string;
}

interface DeviceProfile {
  hardwareConcurrency: number;
  deviceMemory: number | 'unknown';
  pixelRatio: number;
  gpuTier: number;
  details: DeviceDetails;
}

function getiOSMemoryByGPU(gpuModel: string = ''): number | 'unknown' {
  return gpuMemoryMap[gpuModel.toLowerCase()] || 'unknown';
}

function getDeviceMemory(gpuName: string | undefined) {
  if (navigator.deviceMemory) {
    return navigator.deviceMemory;
  }
  // deviceMemory is not available on iOS
  // fallback to a local memory mapping based on the detected GPU name
  return DeviceInfo.isIOS ? getiOSMemoryByGPU(gpuName) : 'unknown';
}

async function getDeviceProfile(): Promise<DeviceProfile> {
  const gpuTier = await getGPUTier();

  const profile: DeviceProfile = {
    hardwareConcurrency: navigator.hardwareConcurrency || 4,
    deviceMemory: getDeviceMemory(gpuTier.gpu),
    pixelRatio: window.devicePixelRatio,
    gpuTier: gpuTier.tier,
    details: {
      gpuModel: gpuTier.gpu,
      fps: gpuTier.fps,
      userAgent: navigator.userAgent,
    },
  };

  return profile;
}

function getCPUScore(cores: number) {
  const score = weights.hardwareConcurrency;
  if (cores >= thresholds.hardwareConcurrency.high) {
    return score;
  }
  return cores >= thresholds.hardwareConcurrency.medium ? score - 1 : 0;
}

function getMemoryScore(deviceMemory: number | 'unknown') {
  const score = weights.deviceMemory;
  if (deviceMemory === 'unknown' || deviceMemory <= thresholds.deviceMemory.low) {
    return score - 2;
  }
  return deviceMemory >= thresholds.deviceMemory.high ? score : score - 1;
}

function getGPUScore(tier: number) {
  const score = weights.gpuTier;
  if (tier >= thresholds.gpuTier.high) {
    return score;
  }
  return tier >= thresholds.gpuTier.medium ? score - 1 : 0;
}

function getPixelRatioScore(pixelRatio: number) {
  const score = weights.pixelRatio;
  if (pixelRatio >= thresholds.pixelRatio.high) {
    return score;
  }
  return pixelRatio >= thresholds.pixelRatio.medium ? score - 0.5 : 0;
}

function getScore(profile: DeviceProfile): number {
  let score = 0;

  score += getCPUScore(profile.hardwareConcurrency);
  score += getMemoryScore(profile.deviceMemory);
  score += getGPUScore(profile.gpuTier);
  score += getPixelRatioScore(profile.pixelRatio);

  if (score >= scores.high.threshold) {
    return scores.high.value;
  }

  return score >= scores.medium.threshold ? scores.medium.value : scores.low.value;
}

export async function getDevicePerformanceData() {
  const profile = await getDeviceProfile();
  const score = getScore(profile);
  const { hardwareConcurrency, gpuTier, deviceMemory, pixelRatio } = profile;
  return {
    level: score,
    details: {
      cpu: hardwareConcurrency,
      gpu: gpuLevels[gpuTier] ?? 'unknown',
      memory: deviceMemory,
      pixelRatio,
    },
  };
}
